<script setup lang="ts">
import {
  ref,
  computed,
} from "vue";
import {
  MessageSquare,
} from "@lucide/vue";

const props = defineProps<{
  form: {
    subject: string;
    description: string;
  };
  formErrors?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: "update:form", value: {subject: string; description: string}): void;
  (e: "submit"): void;
}>();

const {$t} = useNuxtApp();
const formRef = ref();

const rules = computed(() => ({
  subject: [
    {required: true, message: $t("SUPPORT.ERR_SUBJECT_REQ"), trigger: "blur"},
    {min: 3, message: $t("SUPPORT.ERR_SUBJECT_MIN"), trigger: "blur"},
  ],
  description: [
    {required: true, message: $t("SUPPORT.ERR_DESCRIPTION_REQ"), trigger: "blur"},
    {min: 10, message: $t("SUPPORT.ERR_DESCRIPTION_MIN"), trigger: "blur"},
  ],
}));

defineExpose({
  validate: async () => {
    if (!formRef.value) {
      return false;
    }
    return await formRef.value.validate().catch(() => false);
  },
  resetFields: () => {
    formRef.value?.resetFields();
  },
});
</script>

<template>
  <div>
    <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">
      {{ $t('SUPPORT.DIALOG_DESCRIPTION') }}
    </div>

    <el-form
      ref="formRef"
      :model="props.form"
      :rules="rules"
      label-position="top"
      @submit.prevent="emit('submit')"
    >
      <el-form-item
        :label="$t('SUPPORT.FIELD_SUBJECT')"
        prop="subject"
        :error="props.formErrors?.subject"
      >
        <el-input
          :model-value="props.form.subject"
          :placeholder="$t('SUPPORT.PLACEHOLDER_SUBJECT')"
          maxlength="150"
          show-word-limit
          @update:model-value="(val: string) => emit('update:form', { ...props.form, subject: val })"
        >
          <template #prefix>
            <MessageSquare :size="16" />
          </template>
        </el-input>
      </el-form-item>

      <el-form-item
        :label="$t('SUPPORT.FIELD_DESCRIPTION')"
        prop="description"
        :error="props.formErrors?.description"
      >
        <el-input
          :model-value="props.form.description"
          type="textarea"
          :rows="5"
          :placeholder="$t('SUPPORT.PLACEHOLDER_DESCRIPTION')"
          maxlength="2000"
          show-word-limit
          @update:model-value="(val: string) => emit('update:form', { ...props.form, description: val })"
        />
      </el-form-item>
    </el-form>
  </div>
</template>
